<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';
  import { usePreferredReducedMotion } from '@vueuse/core';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import { useDraftActions } from '@/composition/menu-import/use-draft-actions';
  import {
    EMPTY_PROPOSAL_PREVIEW,
    draftListSections,
    isSectionCollapsed,
    newlyOpenSectionKeys,
  } from '@/domain/menu-import/draft-list';
  import type {
    IDraftListSection,
    IProposalPreview,
  } from '@/domain/menu-import/draft-list';
  import { isUpdateDraft } from '@/domain/menu-import/live-update';
  import { filterCounts } from '@/domain/menu-import/menu-draft';
  import type { TItemFilter } from '@/domain/menu-import/menu-draft';
  import type { IMenuDraft } from '@/interfaces/IMenuImport';
  import V2MenuDraftCategory from './V2MenuDraftCategory.vue';
  import V2MenuOthersCard from './V2MenuOthersCard.vue';

  /**
   * The menu being built (plan §4.5): its categories and dishes in menu
   * order under the chosen filter, the pending proposal marks, and at the end
   * «Agregar plato» / «Agregar categoría». An empty draft teaches the first
   * step; a filter without matches offers «Todos». The open and the selected
   * dish stay listed whatever the filter, and a category shown open never
   * collapses under the owner. Dish and category edits go straight to the
   * draft; opening a dish and adding records are the workspace's (they open
   * what they add). An update (plan §3.5) ends with the card of the menu's
   * dishes left out of the photos, under «Todos».
   */
  const props = withDefaults(
    defineProps<{
      draft: IMenuDraft;
      filter: TItemFilter;
      /** What the pending proposals of the latest chat turn would change. */
      preview?: IProposalPreview;
      openItemId?: string | null;
      selectedItemId?: string | null;
      currency: string;
      country: string;
      showAllergens: boolean;
      /** Review lines, inline fixes and the list end (not while reading). */
      showReview: boolean;
      /** A category just added: its name opens for typing. */
      renameCategoryId?: string | null;
      disabled?: boolean;
      /** `.sync`: the card of the menu's dishes left out of the photos is open. */
      othersExpanded?: boolean;
    }>(),
    {
      preview: () => EMPTY_PROPOSAL_PREVIEW,
      openItemId: null,
      selectedItemId: null,
      renameCategoryId: null,
      disabled: false,
      othersExpanded: false,
    }
  );
  const emit = defineEmits<{
    (e: 'open', itemId: string | null): void;
    /** null: the last category. */
    (e: 'add-item', categoryId: string | null): void;
    (e: 'add-category'): void;
    (e: 'rename-started'): void;
    /** «Todos» from the empty filter. */
    (e: 'filter', filter: TItemFilter): void;
    (e: 'update:othersExpanded', expanded: boolean): void;
  }>();
  const reducedMotion = usePreferredReducedMotion();
  const othersCard = ref<InstanceType<typeof V2MenuOthersCard> | null>(null);
  const actions = useDraftActions({ country: () => props.country });

  /** Categories shown open (expanded by the owner, or shown open once): they stay open. */
  const expanded = ref<ReadonlySet<string>>(new Set());

  /** The open dish stays listed whatever the filter until it is closed (its editor never vanishes mid-edit). */
  const keptItemIds = computed(() =>
    props.openItemId !== null ? [props.openItemId] : []
  );
  /** The open and the selected dish: their category is never collapsed. */
  const heldItemIds = computed(() =>
    [props.openItemId, props.selectedItemId].filter(
      (id): id is string => id !== null
    )
  );
  const sections = computed(() =>
    draftListSections(
      props.draft,
      props.filter,
      props.preview,
      keptItemIds.value
    )
  );
  const empty = computed(() => props.draft.categoryOrder.length === 0);
  const noMatch = computed(() => !empty.value && sections.value.length === 0);
  const canAddItem = computed(() => props.draft.categoryOrder.length > 0);
  const allCount = computed(() => filterCounts(props.draft).all);
  const update = computed(() => isUpdateDraft(props.draft));
  /** Kept by reference across hides (`applyDraftDelta`): a hide never redraws every row. */
  const liveOthers = computed(() =>
    props.draft.live ? props.draft.live.others : null
  );
  const showOthers = computed(
    () => props.showReview && update.value && props.filter === 'all'
  );

  function isCollapsed(section: IDraftListSection): boolean {
    return isSectionCollapsed(
      section,
      props.filter,
      expanded.value,
      heldItemIds.value
    );
  }

  function expand(key: string) {
    expanded.value = new Set([...expanded.value, key]);
  }

  // A category shown open stays open (its last flagged dish fixed, dishes added).
  watch(
    sections,
    (list) => {
      const opened = newlyOpenSectionKeys(
        list,
        props.filter,
        expanded.value,
        heldItemIds.value
      );
      if (opened.length > 0)
        expanded.value = new Set([...expanded.value, ...opened]);
    },
    { immediate: true }
  );

  /** One dish open at a time; tapping the open one closes it. */
  function toggleOpen(itemId: string) {
    emit('open', props.openItemId === itemId ? null : itemId);
  }

  function renameCategory(categoryId: string, name: string) {
    void actions.renameCategory(categoryId, name);
  }

  function removeCategory(categoryId: string) {
    void actions.removeCategory(categoryId);
  }

  function confirmItem(itemId: string) {
    void actions.confirmItem(itemId);
  }

  function setItemPrice(itemId: string, price: number) {
    void actions.setItemPrice(itemId, price);
  }

  function hideItem(itemId: string) {
    void actions.hideItem(itemId);
  }

  function linkItem(itemId: string, liveItemId: number) {
    void actions.linkLive(itemId, liveItemId);
  }

  function makeNew(itemId: string) {
    void actions.linkLive(itemId, null);
  }

  function keepPrice(itemId: string) {
    void actions.keepLivePrice(itemId);
  }

  function keepSizes(itemId: string) {
    void actions.keepLiveSizes(itemId);
  }

  function showItem(itemId: string) {
    void actions.showItem(itemId);
  }

  /** The category holding the focus (an inline price being typed). */
  function focusedCategoryId(): string | null {
    const active = document.activeElement;
    const block =
      active instanceof Element
        ? active.closest('.v2-menu-draft-category[data-category-id]')
        : null;
    return block ? block.getAttribute('data-category-id') : null;
  }

  // The draft became an update (the read compared it with the menu): the
  // categories that only repeat the menu fold, unless they hold the open or
  // the selected dish, or the focus.
  watch(update, (isUpdate) => {
    if (!isUpdate) return;
    const focused = focusedCategoryId();
    const folding = sections.value
      .filter(
        (section) =>
          section.defaultCollapsed &&
          section.key !== focused &&
          !section.rows.some(
            (row) => !row.ghost && heldItemIds.value.includes(row.item.id)
          )
      )
      .map((section) => section.key);
    if (folding.some((key) => expanded.value.has(key)))
      expanded.value = new Set(
        [...expanded.value].filter((key) => !folding.includes(key))
      );
  });

  /** The summary's «{count} platos no están en las fotos» brings the card into view. */
  async function scrollToOthers() {
    await nextTick();
    const element = othersCard.value?.$el;
    if (!(element instanceof HTMLElement)) return;
    element.scrollIntoView({
      block: 'start',
      behavior: reducedMotion.value === 'reduce' ? 'auto' : 'smooth',
    });
    othersCard.value?.focusTitle();
  }

  defineExpose({ scrollToOthers });

  // A store change or a reload starts with every big category collapsed again.
  watch(
    () => props.draft.categoryOrder,
    (order) => {
      const known = new Set(order);
      if ([...expanded.value].some((key) => !known.has(key)))
        expanded.value = new Set(
          [...expanded.value].filter((key) => known.has(key))
        );
    }
  );
</script>

<template>
  <div class="v2-menu-draft-list">
    <PosUiEmptyState
      v-if="empty"
      class="list-empty"
      icon="silverware-fork-knife"
      :title="$t('menuImport.review.emptyTitle')"
    >
      <template v-if="showReview" #action>
        <PosUiButton
          color="neutral"
          icon="plus"
          :disabled="disabled"
          @click="emit('add-category')"
          >{{ $t('menuImport.review.addCategory') }}</PosUiButton
        >
      </template>
    </PosUiEmptyState>
    <PosUiEmptyState
      v-else-if="noMatch"
      class="list-empty"
      icon="filter-variant-remove"
      :title="$t('menuImport.filters.empty')"
    >
      <template #action>
        <PosUiButton
          color="neutral"
          variant="outline"
          @click="emit('filter', 'all')"
          >{{ $t('menuImport.filters.all', { count: allCount }) }}</PosUiButton
        >
      </template>
    </PosUiEmptyState>
    <template v-else>
      <V2MenuDraftCategory
        v-for="section in sections"
        :key="section.key"
        class="list-category"
        :category="section.category"
        :rows="section.rows"
        :total="section.total"
        :to-check="section.toCheck"
        :price-changed="section.priceChanged"
        :unchanged="section.unchanged"
        :ghost="section.ghost"
        :collapsed="isCollapsed(section)"
        :addon-groups="draft.addonGroups"
        :draft="draft"
        :open-item-id="openItemId"
        :selected-item-id="selectedItemId"
        :currency="currency"
        :country="country"
        :show-review="showReview"
        :show-allergens="showAllergens"
        :start-rename="!section.ghost && section.key === renameCategoryId"
        :disabled="disabled"
        :live-others="liveOthers"
        @rename="renameCategory(section.category.id, $event)"
        @remove="removeCategory(section.category.id)"
        @add-item="emit('add-item', section.category.id)"
        @open="toggleOpen"
        @expand="expand(section.key)"
        @rename-started="emit('rename-started')"
        @confirm="confirmItem"
        @set-price="setItemPrice"
        @hide="hideItem"
        @link="linkItem"
        @new="makeNew"
        @keep-price="keepPrice"
        @keep-sizes="keepSizes"
        @show="showItem"
      />
      <V2MenuOthersCard
        v-if="showOthers"
        ref="othersCard"
        :draft="draft"
        :expanded="othersExpanded"
        :currency="currency"
        :country="country"
        @update:expanded="emit('update:othersExpanded', $event)"
      />
      <div v-if="showReview" class="list-end">
        <PosUiButton
          v-if="canAddItem"
          color="neutral"
          variant="outline"
          icon="plus"
          :disabled="disabled"
          @click="emit('add-item', null)"
          >{{ $t('menuImport.review.addItem') }}</PosUiButton
        >
        <PosUiButton
          color="neutral"
          variant="ghost"
          icon="plus"
          :disabled="disabled"
          @click="emit('add-category')"
          >{{ $t('menuImport.review.addCategory') }}</PosUiButton
        >
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-draft-list {
    @apply tw-flex tw-flex-col tw-gap-5;
    .list-empty {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
    }
    .list-end {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
  }
</style>
